import map from 'lodash/map'
import merge from 'lodash/merge'
import get from 'lodash/get'
import type { GetOptionLabel, GroupBase, Props, MultiValue, StylesConfig } from 'react-select'
import { components } from 'react-select'
import type { FunctionComponent, RefObject } from 'react'
import React from 'react'
import { useTheme } from '@ztechapps/common.hook'
import { getWindow } from '@ztechapps/common.utils'
import type { ApolloClient, FetchPolicy } from '@apollo/client'
import type { AsyncProps } from 'react-select/async'
import type { CreatableProps } from 'react-select/creatable'
import type { FilterQuery, IOption, QueryOptions } from '@ztechapps/common.contract'

export type DataLoadOptions<T = any> = {
  filter?: ({ input }: { input: Record<any, any> }) => FilterQuery<T>
  resultFieldName?: string
  options?: ({ input }: { input?: Record<any, any> }) => QueryOptions<T>
  fetchPolicy?: FetchPolicy
}

export const useThemeColors = (defaultTheme: any) => {
  const { colors } = useTheme()

  if (defaultTheme) {
    return defaultTheme
  }

  let theme = customTheme
  if (colors) {
    theme = (e) => {
      return {
        ...e,
        colors: {
          ...e.colors,
          ...colors,
        },
      }
    }
  }

  return theme
}

export const customTheme = (theme) => {
  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary: '#81352E',
      primary75: '#AE7E7A',
      primary50: '#BA908D',
      primary25: '#C5A3A0',
    },
  }
}

export const setupOptionLabel = (labelKey: string) => (option: SelectOption) => get(option, labelKey)
export const setupLoadOptions =
  ({
    client,
    dataLoadOptions,
    getOptionLabel,
    onLoadSettled,
    latestRequest,
  }: {
    client: ApolloClient<object>
    dataLoadOptions: DataLoadOptions
    getOptionLabel: GetOptionLabel<SelectOption>
    onLoadSettled?: (failed: boolean) => void
    /** Shared across renders by the caller — only the newest request may settle, as react-select ignores older callbacks. */
    latestRequest: RefObject<number>
  }) =>
  (input, callback) => {
    const requestId = ++latestRequest.current
    const settle = (failed: boolean) => {
      if (requestId === latestRequest.current) onLoadSettled?.(failed)
    }
    const dataLoadOptionsFilter = get(dataLoadOptions, 'filter')
    const query = get(dataLoadOptions, 'query')
    const resultFieldName = get(dataLoadOptions, 'resultFieldName')
    const filter = dataLoadOptionsFilter({ input })
    const options: any = merge({}, { limit: 20 }, dataLoadOptions?.options?.({ input }) ?? {})

    const fetchPolicy = dataLoadOptions?.fetchPolicy ?? 'cache-first'

    client
      .query({ query, variables: { filter, options }, fetchPolicy })
      .then((results) => {
        let items = get(results, `data.${resultFieldName}`)

        //just remap to value, label
        items = map(items, (e) => ({
          value: e?._id,
          label: getOptionLabel(e),
          ...e,
        }))

        callback(items)
        settle(false)
      })
      .catch(() => {
        settle(true)
        callback()
      })
  }

export const OptionComponent = ({ data, ...rest }: any) => {
  //always set the isSelected to false so it wont highlight
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return <components.Option {...{ ...rest, data, isSelected: false }}>{rest?.label}</components.Option>
}

const VIEWPORT_MARGIN = 8

// A control past this much room to its right is treated as "too close to the edge" and the menu
// opens leftward from the control's right edge instead of its usual rightward growth from the
// control's LEFT edge (a menu that size or smaller never needed the anchor at all). Below it,
// react-select has no horizontal collision handling of its own, so a `width: 'max-content'` menu
// can run past the viewport's right edge. Anchoring EVERY menu, unconditionally, is wrong the
// other way: it broke a short/left-positioned control's default (still comfortably on-screen)
// menu. The default (450) fits the shared `Select`'s own menus; a caller whose menu is
// deliberately WIDER than its control (`ProductSelect`'s `minWidth: 600`) passes its own larger
// threshold instead — `ProductSelect` passes `950`.
const DEFAULT_ANCHOR_THRESHOLD = 450
const hasRoomOnRight = (rect: { left: number }, anchorThreshold: number) =>
  getWindow().innerWidth - rect.left >= anchorThreshold

// react-select's own `menuPortal` style-callback state shape (`StylesConfig['menuPortal']`'s
// second parameter) — `rect` is `{ left, width }`, not `{ left, right }`, so every helper below
// computes the right edge from those two instead of assuming one exists.
type MenuPortalStyleArgs = Parameters<NonNullable<StylesConfig['menuPortal']>>[1]
type ControlRect = { left: number; width: number }

const rightEdgeOf = (rect: ControlRect) => rect.left + rect.width
const rightOffsetFor = (rect: ControlRect) => Math.max(VIEWPORT_MARGIN, getWindow().innerWidth - rightEdgeOf(rect))
const maxWidthFromRightOffset = (rightOffset: number) => `calc(100vw - ${rightOffset + VIEWPORT_MARGIN}px)`
// The has-room branch grows the menu rightward from the control's own left edge with no cap of
// its own — bounded here by the same viewport-margin rule, measured from that left edge instead.
const maxWidthFromLeft = (left: number) => `calc(100vw - ${left + VIEWPORT_MARGIN}px)`

// The `menuPortal` style writes these on the portal wrapper from react-select's live `rect`,
// and the menu — a DOM child of that wrapper — inherits them through `var()` with fallbacks.
// react-select re-renders `MenuPortal` alone on resize, scroll and element shifts, so a menu
// element built by an earlier `Select` render keeps a stale cap when it reads one off a closure.
// Precedent: `libs/webui/util/src/RouteOffCanvas/index.tsx:82` writes `--offcanvas-width`, read
// by `libs/webui/util/src/RouteOffCanvas/RouteOffCanvas.module.scss:21`.
const MENU_MAX_WIDTH_VAR = '--select-menu-max-width'
const MENU_RIGHT_VAR = '--select-menu-right'

// For the PORTAL: `right` is a viewport offset only under `menuPosition: 'fixed'`
// (`ProductSelect`), whose containing block is the viewport; under react-select's default
// `'absolute'` it is measured from the wrapper's containing block.
const rightAnchorPortalStyle = (rect: ControlRect) => {
  const right = rightOffsetFor(rect)
  return {
    left: 'auto',
    right: `${right}px`,
    maxWidth: maxWidthFromRightOffset(right),
    [MENU_MAX_WIDTH_VAR]: maxWidthFromRightOffset(right),
    // `0px` flushes the menu to the wrapper's own right edge, which already carries the
    // right-anchor offset above — re-applying the viewport offset on the menu would double-count it.
    [MENU_RIGHT_VAR]: '0px',
  }
}

// `menuBase` is the caller's own per-menu styling (`mergeStyle`'s theme colours and
// `isMaxWidth`-driven width; `ProductSelect`'s fixed `minWidth: 600`) layered under the anchor.
// `anchorThreshold`: see `DEFAULT_ANCHOR_THRESHOLD`'s doc comment above.
// react-select's default `menu` style sets no `left` or `right`
// (`node_modules/react-select/dist/index-641ee5b8.esm.js:485-501`), so the menu needs `right` alone.
export const createRightAnchorMenuStyles = (
  isMaxWidth: boolean,
  menuBase: (provided: any) => any,
  anchorThreshold: number = DEFAULT_ANCHOR_THRESHOLD
) => {
  return {
    menu: (provided: any) => ({
      ...menuBase(provided),
      maxWidth: `var(${MENU_MAX_WIDTH_VAR}, none)`,
      right: `var(${MENU_RIGHT_VAR}, auto)`,
    }),

    menuPortal: (provided: any, state: MenuPortalStyleArgs) => {
      // Only the `isMaxWidth` menu has a bound worth positioning against — the non-`isMaxWidth`
      // menu is `minWidth: '100%'` of the control, which never exceeds the control's own
      // right-edge-safe position.
      if (!isMaxWidth) return { ...provided, zIndex: 9999 }
      if (hasRoomOnRight(state.rect, anchorThreshold)) {
        return { ...provided, zIndex: 9999, [MENU_MAX_WIDTH_VAR]: maxWidthFromLeft(state.rect.left) }
      }
      return { ...provided, zIndex: 9999, ...rightAnchorPortalStyle(state.rect) }
    },
  }
}

// A caller's `menu` / `menuPortal` runs on the shared style's output, so the live cap and the
// shared menu base reach every caller that spreads `provided` — every other style key still
// replaces the shared one through the `merge` below.
const layerStyle = (
  sharedStyle: (provided: any, state: any) => any,
  callerStyle?: (provided: any, state: any) => any
) => (callerStyle ? (provided: any, state: any) => callerStyle(sharedStyle(provided, state), state) : sharedStyle)

export const mergeStyle = (newStyle, isMaxWidth) => {
  newStyle = newStyle ?? {}
  const { menu, menuPortal } = createRightAnchorMenuStyles(isMaxWidth, (provided) => ({
    ...provided,
    zIndex: 400,
    minWidth: '100%',
    width: isMaxWidth ? 'max-content' : '',
    color: 'var(--bs-body-color)',
    backgroundColor: `var(--bs-light-bg-subtle)`,
    marginTop: '0px',
  }))
  const defaultStyle = {
    control: (provided, { isDisabled }) => ({
      ...provided,
      backgroundColor: isDisabled ? 'var(--bs-secondary-bg)' : `var(--bs-body-bg)`,
      color: 'var(--bs-body-color)',
      border: 'var(--bs-border-width) solid var(--bs-border-color)',
      borderRadius: 'var(--bs-border-radius)',
      boxShadow: 'none',
      width: '100%',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      ':hover': {
        borderColor: 'color-mix(in srgb, var(--bs-primary), white 50%)',
      },
    }),

    menu,
    menuPortal,

    multiValueRemove: (provided) => ({
      ...provided,
      color: 'var(--bs-danger)',
    }),

    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      color: 'var(--bs-body-color)',
      backgroundColor: isFocused ? 'var(--bs-warning-bg-subtle)' : isSelected ? 'var(--bs-primary-bg-subtle)' : '',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),

    indicatorSeparator: (provided) => ({ ...provided, marginTop: '0px', marginBottom: '0px' }),

    singleValue: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),
  }

  return {
    ...merge({}, defaultStyle, newStyle),
    menu: layerStyle(menu, newStyle.menu),
    menuPortal: layerStyle(menuPortal, newStyle.menuPortal),
  }
}

export type SelectOption<T = unknown> = IOption & Record<any, any> & T

export type SelectCreatableOption<T = unknown> = SelectOption<T> & {
  __isCreate?: boolean
}

type SelectPropsBasic<
  Option = unknown,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> & {
  submitting?: boolean
  disabled?: boolean
  isMaxWidth?: boolean
  components?: any
}

export type SelectProps<
  Option = SelectOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = SelectPropsBasic<Option, IsMulti, Group> & {
  labelKey?: string
  valueKey?: string
}

export type SelectAsyncProps<
  Option = SelectOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = SelectProps<Option, IsMulti, Group> &
  AsyncProps<Option, IsMulti, Group> & {
    labelKey?: string
    dataLoadOptions?: DataLoadOptions
    filter?: FilterQuery<any>
  }

export type SelectCreatableProps<
  Option = SelectCreatableOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = SelectProps<Option, IsMulti, Group> &
  CreatableProps<Option, IsMulti, Group> & {
    //use this if you want to get the actual value that was returned from the server operation,
    //onCreateOption will just return the local string value that was typed in in drop-down
    onServerCreateOption?: (value: Option | MultiValue<Option>) => void
  }

export type SelectAsyncCreatableProps<
  Option = SelectCreatableOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = SelectAsyncProps<Option, IsMulti, Group> & SelectCreatableProps<Option, IsMulti, Group>

export type SelectFunctionComponents = FunctionComponent<
  SelectProps | SelectCreatableProps | SelectAsyncProps | SelectAsyncCreatableProps
>
export * from './Select'
